import { generateElementSelectors } from '../styles/selectors'
import Attrs from '../models/Attrs'
import { iconToString } from '../helpers/icon'
import getPresetButtons from '../helpers/buttons'
import type {
  BaseBuilderVersion,
  Flow,
  FlowPage,
  OptionSelector,
  OptionSelectorButton,
} from '../models/types'
import replaceStorageURLsFromFirebaseToCloudflare from '../helpers/replaceStorageURLsFromFirebaseToCloudflare'
import renderTemplate from '../helpers/templating'
import { addLanguageController, getBaseBuilderVersion } from '../helpers/utils'

const CONTROLLER = 'components--option-selector'
const BUTTONS_REPEATABLE_CONTROLLER = 'button-repeatable'
export default function renderOptionSelector(
  component: OptionSelector,
  attrs: Attrs,
  page: FlowPage,
  flow: Flow,
  editingMode: boolean,
  userData?: { [key: string]: unknown }
): string {
  const layout = !component.layout ? 'button_tiles' : component.layout
  attrs
    .addController(CONTROLLER)
    .addClass(CONTROLLER, 'selected', 'selected')
    .addDOMClass(layout)
    .addAction(
      `${CONTROLLER}:next->flow#next`,
      `${CONTROLLER}:update->flow#update`,
      `${CONTROLLER}:actions->flow#dispatchAction`
    )
    .addValue(CONTROLLER, 'multiple', `${Boolean(component.multiple)}`)

    .addValue(CONTROLLER, 'searchable', `${component.allow_typing !== false}`)
    .addValueIfExists(CONTROLLER, 'default-val', component.defaultVal)
  if (flow.disable_form_validation) attrs.add('custom-validity', `true`)
  if (component.allow_deselect) attrs.addValue(CONTROLLER, 'allow-deselect', `true`)
  if (typeof component.maxSelection === 'number')
    attrs.addValue(CONTROLLER, 'max-selections', `${component.maxSelection}`)
  if (component.button_repeater_key) {
    attrs.addController(BUTTONS_REPEATABLE_CONTROLLER)
    // attrs.addValue('button-repeatable', `url`, `${context.origin}/${context.flowId}`)
    attrs.addValue(BUTTONS_REPEATABLE_CONTROLLER, 'page-id', page.id)
    attrs.addValue(BUTTONS_REPEATABLE_CONTROLLER, 'component-id', component.id)
    attrs.addValue(
      BUTTONS_REPEATABLE_CONTROLLER,
      'component',
      JSON.stringify(component).replace(/"/g, '&quot;')
    )
    attrs.addValue(BUTTONS_REPEATABLE_CONTROLLER, 'repeater-key', component.button_repeater_key)
    attrs.addAction(`${BUTTONS_REPEATABLE_CONTROLLER}:actions->flow#dispatchAction`)
  }

  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  //* Label element generation only if component has label on JSON
  const label = getLabelHTML(component, page, attrs, editingMode, builderVersion, userData)

  const multiple = !!component.multiple
  const searchable = component.allow_typing !== false
  const shouldUseAdvancedDropdown = !!component.is_advanced_dropdown || multiple || searchable

  const optionSelector = getOptionSelector(
    component,
    page,
    attrs,
    editingMode,
    userData,
    shouldUseAdvancedDropdown,
    multiple,
    searchable,
    builderVersion
  )

  return `<div ${attrs}>${label}${optionSelector}</div>`
}

function getOptionSelector(
  component: OptionSelector,
  page: FlowPage,
  attrs: Attrs,
  editingMode: boolean,
  userData: { [key: string]: unknown } | undefined,
  shouldUseAdvancedDropdown: boolean,
  multiple: boolean,
  searchable: boolean,
  builderVersion: BaseBuilderVersion
) {
  if (component.dropdown) {
    if (shouldUseAdvancedDropdown) {
      return getOSTypeDropdownAdvanced(
        component,
        page,
        attrs,
        editingMode,
        multiple,
        searchable,
        builderVersion,
        userData
      )
    } else {
      return getOSTypeDropdownRegular(component, page, attrs, builderVersion, userData)
    }
  }

  return getOSTypeButtons(component, page, attrs, editingMode, builderVersion, userData)
}

function getLabelHTML(
  component: OptionSelector,
  page: FlowPage,
  attrs: Attrs,
  editingMode: boolean,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
) {
  if (component.label) {
    const labelText = renderTemplate(component.label, userData)
    const labelAttrs = new Attrs(attrs.static_render).addDOMClass(
      ...generateElementSelectors({
        elementType: 'Label',
        component,
        page,
        builderVersion,
      })
    )
    if (component.page_reactive_classes) {
      labelAttrs.addReactiveElement()
    }

    //* Add language controller for the label
    addLanguageController({ attrs: labelAttrs, component, keys: ['label'] })

    //* Add template controller if the label text has {{
    if (component.label && component.label.includes('{{'))
      labelAttrs
        .addController('template')
        .addValueIfExists('template', 'text', encodeURIComponent(component.label || ''))

    return `<label ${labelAttrs}>${labelText}</label>`
  } else return ''
}

function getOSTypeButtons(
  component: OptionSelector,
  page: FlowPage,
  attrs: Attrs,
  editingMode: boolean,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
) {
  let buttonsArray = getButtons(component).map((b, index) =>
    renderButton(b, component, page, editingMode, attrs, builderVersion, userData)
  )

  //* if randomize_buttons is true, shuffle the buttons
  if (component.randomize_buttons) buttonsArray = buttonsArray.sort(() => Math.random() - 0.5)

  const buttons = buttonsArray.join('')

  const optionButtonListAttrs = new Attrs(attrs.static_render).addDOMClass(
    generateElementSelectors({ elementType: 'OptionButtonList', component, page, builderVersion }),
    'buttons'
  )
  if (component.page_reactive_classes) optionButtonListAttrs.addReactiveElement()

  return `<div ${optionButtonListAttrs}>${buttons}</div>`
}

function renderButton(
  button: OptionSelectorButton,
  component: OptionSelector,
  page: FlowPage,
  editingMode: boolean,
  attrs: Attrs,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
): string {
  const useButtonElement = !!component.use_button_elements
  const tag = useButtonElement ? 'button' : 'div'

  const classes = generateElementSelectors({
    elementType: 'OptionButtonCard',
    component,
    page,
    elementTags: ['Button'],
    elementExtras: {
      Key: `${button.key}`,
    },
    builderVersion,
  })

  const buttonAttrs = new Attrs(attrs.static_render)
    .addAction(`click->${CONTROLLER}#selectOption`, `keydown->${CONTROLLER}#evaluateRunningActions`)
    .addParam(CONTROLLER, `key`, component.key)
    .addParam(CONTROLLER, `value`, button.key || '')
    .addParam(CONTROLLER, `id`, button.id || '')
    .addTarget(CONTROLLER, `option`)
    .add(`class`, [`ButtonCard`, `button-animate`, ...classes])
  if (useButtonElement) {
    //* Add type button to avoid it defaulting to submit inside a form
    buttonAttrs.add('type', 'button')
  }
  else buttonAttrs.add('tabindex', '0')
  if (component.page_reactive_classes) buttonAttrs.addReactiveElement()

  if (button.conditions) buttonAttrs.addConditions(button.conditions)
  if (button.single_select)
    buttonAttrs.addParam(CONTROLLER, `single-select`, `${button.single_select}`)
  if (button.triggerEvent) {
    buttonAttrs.addParam(CONTROLLER, `trigger-event`, button.triggerEvent)
    if (button.triggerEvent === 'open-url' && button.url) {
      buttonAttrs.addParam(CONTROLLER, 'url', button.url)
      if (button.openUrlInNewTab) buttonAttrs.addParam(CONTROLLER, 'new-tab', 'true')
    }
  }
  if (button.hide) buttonAttrs.add('style', 'display: none;')
  if (button.is_repeatable_button) buttonAttrs.addValue('repeatable', 'button', 'true')

  const checkbox = component.checkbox
    ? renderCheckbox(component, page, button, attrs.static_render, builderVersion)
    : ''
  const { text, description } = renderButtonTextContent(
    button,
    component,
    page,
    editingMode,
    attrs,
    builderVersion,
    userData
  )
  const buttonIcon = renderButtonIcon(button, component, page, attrs, builderVersion, userData)
  return `<${tag} ${buttonAttrs}>${component.checkbox_location === 'start' ? checkbox : ''
    }${buttonIcon}${text}${description}${component.checkbox_location === 'end' ? checkbox : ''}</${tag}>`
}

function renderButtonTextContent(
  button: OptionSelectorButton,
  component: OptionSelector,
  page: FlowPage,
  editingMode: boolean,
  attrs: Attrs,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
): { text: string; description: string } {
  const returnObj = { text: '', description: '' }
  if (button.text) {
    const textClasses = generateElementSelectors({
      elementType: 'OptionButtonCardText',
      component,
      page,
      elementTags: ['ButtonText'],
      elementExtras: { Key: `${button.key}` },
      builderVersion,
    })

    const textWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(button.text)
    const textAfterTemplate =
      replaceStorageURLsFromFirebaseToCloudflare(
        renderTemplate(textWithURLReplacement, userData)
      ) || ''

    const textAttrs = new Attrs(attrs.static_render).addDOMClass(textClasses)

    //* Add language controller for the text
    addLanguageController({ attrs: textAttrs, component: button, keys: ['text'] })

    //* Add template controller if the text has {{
    if (button.text && button.text.includes('{{'))
      textAttrs
        .addController('template')
        .addValueIfExists('template', 'text', encodeURIComponent(textWithURLReplacement || ''))
        .addValueIfExists('template', 'always-rerender', component.always_rerender)

    if (component.page_reactive_classes) textAttrs.addReactiveElement()

    //* I add attributes to specify if text is custom HTML
    if (component.richText) textAttrs.add('data-custom-html-text', 'true')

    const text = `<div ${textAttrs}>${textAfterTemplate}</div>`
    returnObj.text = text
  }
  if (button.description) {
    const descriptionClasses = generateElementSelectors({
      elementType: 'OptionButtonCardDescription',
      component,
      page,
      elementTags: ['ButtonDescription'],
      elementExtras: { Key: `${button.key}` },
      builderVersion,
    })

    const descriptionWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(
      button.description
    )
    const descriptionAfterTemplate =
      replaceStorageURLsFromFirebaseToCloudflare(
        renderTemplate(descriptionWithURLReplacement, userData)
      ) || ''

    const descriptionAttrs = new Attrs(attrs.static_render).addDOMClass(
      'description',
      descriptionClasses
    )

    //* Add language controller for the description
    addLanguageController({ attrs: descriptionAttrs, component: button, keys: ['description'] })

    //* Add template controller if the description has {{
    if (button.description && button.description.includes('{{'))
      descriptionAttrs
        .addController('template')
        .addValueIfExists(
          'template',
          'text',
          encodeURIComponent(descriptionWithURLReplacement || '')
        )
        .addValueIfExists('template', 'always-rerender', component.always_rerender)

    if (component.page_reactive_classes) descriptionAttrs.addReactiveElement()

    //* I add attributes to specify if description is custom HTML
    if (component.richDescription) descriptionAttrs.add('data-custom-html-description', 'true')

    const description = `<div ${descriptionAttrs}>${descriptionAfterTemplate}</div>`
    returnObj.description = description
  }

  return returnObj
}

function renderButtonIcon(
  button: OptionSelectorButton,
  component: OptionSelector,
  page: FlowPage,
  attrs: Attrs,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
): string {
  const srcWithURLReplacement = replaceStorageURLsFromFirebaseToCloudflare(button.imageUrl) || ''
  const srcAfterTemplate = (replaceStorageURLsFromFirebaseToCloudflare(
    renderTemplate(srcWithURLReplacement, userData)
  ) || '') as string
  const iconAttrs = new Attrs(attrs.static_render).addDOMClass(['button-icon'])

  //* Add template controller if the src has {{
  if (button.imageUrl && button.imageUrl.includes('{{'))
    iconAttrs
      .addController('template')
      .addValue('template', 'target', 'src')
      .addValueIfExists('template', 'text', encodeURIComponent(srcWithURLReplacement || ''))

  if (component.page_reactive_classes) iconAttrs.addReactiveElement()
  if (button.imageUrl) {
    iconAttrs.addDOMClass([
      'img',
      ...generateElementSelectors({
        elementType: 'OptionButtonCardIconImage',
        component,
        page,
        elementTags: ['GeneralButtonIcon', 'OptionButtonCardIcon', 'ButtonIconImage', 'Icon'],
        elementExtras: { Key: `${button.key}` },
        builderVersion,
      }),
    ])
    const imgAttrs = new Attrs(attrs.static_render)
      .add(
        'class',
        generateElementSelectors({
          elementType: 'OptionButtonCardIconImageElement',
          component,
          page,
          elementTags: [],
          elementExtras: { Key: `${button.key}` },
          builderVersion,
        })
      )
      .add('src', srcAfterTemplate)

    //* Add language controller for the imageUrl and imageAltText
    addLanguageController({
      attrs: imgAttrs,
      component: button,
      keys: ['imageUrl', 'imageAltText'],
      mapKeys: {
        imageUrl: 'src',
        imageAltText: 'alt_text',
      },
    })

    if (button.imageAltText) imgAttrs.add('alt', button.imageAltText)
    if (component.page_reactive_classes) imgAttrs.addReactiveElement()

    return `<span ${iconAttrs}><img ${imgAttrs}></span>`
  } else if (button.emojiIcon) {
    iconAttrs.add(
      'class',
      generateElementSelectors({
        elementType: 'OptionButtonCardIcon',
        component,
        page,
        elementTags: ['GeneralButtonIcon', 'OptionButtonCardIcon', 'Icon'],
        elementExtras: { Key: `${button.key}` },
        builderVersion,
      })
    )
    return `<span ${iconAttrs}>${button.emojiIcon}</span>`
  } else if (button.icon) {
    let [library, name] = iconToString(button.icon)
    if (!name) {
      name = library
      library = 'fa'
    }

    const iconClasses = generateElementSelectors({
      elementType: 'OptionButtonCardIcon',
      component,
      page,
      elementTags: ['GeneralButtonIcon', 'OptionButtonCardIcon', 'Icon'],
      elementExtras: { Key: `${button.key}` },
      builderVersion,
    })

    iconAttrs.addIcon(library, name).addDOMClass(iconClasses)

    return `<div ${iconAttrs}></div>`
  }
  return ''
}

function renderCheckbox(
  component: OptionSelector,
  page: FlowPage,
  button: OptionSelectorButton,
  attrsStaticRender: boolean,
  builderVersion: BaseBuilderVersion
): string {
  const attrs = new Attrs(attrsStaticRender)
    .addController('icon')
    .addDOMClass([
      'Checkbox',
      ...generateElementSelectors({
        elementType: 'OptionButtonCardCheckbox',
        component,
        page,
        elementTags: ['Checkbox'],
        elementExtras: { Key: `${button.key}` },
        builderVersion,
      }),
    ])
    .addValue('icon', 'library', 'fa-solid')
    .addValue('icon', 'name', 'check')
    .addValue('icon', 'visible', 'false')
  if (component.page_reactive_classes) attrs.addReactiveElement()
  return `<div ${attrs}></div>`
}

function getOSTypeDropdownRegular(
  component: OptionSelector,
  page: FlowPage,
  attrs: Attrs,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
): string {
  attrs.addValue(CONTROLLER, 'dropdown', 'true')

  const selectAttrs = new Attrs(attrs.static_render)
    .addDOMClass(
      generateElementSelectors({
        elementType: 'OptionDropdown',
        component,
        page,
        elementTags: ['Dropdown'],
        builderVersion,
      })
    )
    .addParam(CONTROLLER, `key`, component.key)
    .addAction(`change->${CONTROLLER}#selectOption`)
    .addTarget(CONTROLLER, 'select')
    .add('id', `select-${component.id}`)
    .add('name', `select-${component.key}`)
  if (component.always_disabled) selectAttrs.add('disabled', 'true')
  if (component.page_reactive_classes) selectAttrs.addReactiveElement()
  if (component.multiple) selectAttrs.add('multiple', 'true')
  if (component.isRequired) selectAttrs.add('required', 'required')
  const options =
    getButtons(component)
      ?.map((button) => {
        const buttonTextAfterTemplate = renderTemplate(button.text, userData)
        const buttonAttrs = new Attrs(attrs.static_render)
          .add('value', button.key || '')
          .addParam(CONTROLLER, `key`, component.key)
          .addParam(CONTROLLER, `value`, button.key || '')
          .addTarget(CONTROLLER, `option`)
          .addAction(`keydown->${CONTROLLER}#advancedDropdownAccessibility`)

        //* Placeholder option uses component.placeholder with lang variants; regular options use button.text
        if (button.id === '_placeholder_option') {
          addLanguageController({ attrs: buttonAttrs, component, keys: ['placeholder'] })
        } else {
          addLanguageController({ attrs: buttonAttrs, component: button, keys: ['text'] })
        }

        //* Add template controller if the text has {{
        if (button.text && button.text.includes('{{'))
          buttonAttrs
            .addController('template')
            .addValueIfExists('template', 'text', encodeURIComponent(button.text || ''))

        if (button.conditions) buttonAttrs.addConditions(button.conditions)
        if (button.single_select)
          buttonAttrs.addParam(CONTROLLER, `single-select`, `${button.single_select}`)
        if (button.triggerEvent) {
          buttonAttrs.addParam(CONTROLLER, `trigger-event`, button.triggerEvent)
          if (button.triggerEvent === 'open-url' && button.url) {
            buttonAttrs.addParam(CONTROLLER, 'url', button.url)
            if (button.openUrlInNewTab) buttonAttrs.addParam(CONTROLLER, 'new-tab', 'true')
          }
        }
        return `<option ${buttonAttrs}>${buttonTextAfterTemplate}</option>`
      })
      .join('') || ''
  return `<select ${selectAttrs}>${options}</select>`
}

function getOSTypeDropdownAdvanced(
  component: OptionSelector,
  page: FlowPage,
  attrs: Attrs,
  editingMode: boolean,
  mutiple: boolean,
  searchable: boolean,
  builderVersion: BaseBuilderVersion,
  userData?: { [key: string]: unknown }
): string {
  attrs.addValue(CONTROLLER, 'dropdown', 'true')
  attrs.addValue(CONTROLLER, 'advanced-dropdown', 'true')

  const options = getButtons(component) || []

  const defaultText = component.defaultVal
    ? options.find((option) => option.key === component.defaultVal)?.text
    : ''

  const wrapperAttrs = new Attrs(attrs.static_render)
    .addDOMClass(
      generateElementSelectors({
        elementType: 'OptionDropdown',
        component,
        page,
        elementTags: ['Dropdown'],
        builderVersion,
      })
    )
    .addDOMClass('select')
    .addParam(CONTROLLER, `key`, component.key)
    .add('id', `select-${component.id}`)
    .add('name', `select-${component.key}`)
    .addAction(`click->${CONTROLLER}#toggleAdvancedDropdown`)
    .addAction(`keyup->${CONTROLLER}#keyPressOnAdvancedDropdown`)

  if (component.always_disabled) wrapperAttrs.add('disabled', 'true')
  if (component.page_reactive_classes) wrapperAttrs.addReactiveElement()
  if (component.multiple) wrapperAttrs.add('multiple', 'true')
  if (component.isRequired) wrapperAttrs.add('required', 'required')
  if (!searchable) {
    wrapperAttrs.add('tabIndex', '0')
    wrapperAttrs.addAction(`keydown->${CONTROLLER}#advancedDropdownAccessibility`)
  }

  const headerAttrs = new Attrs(attrs.static_render)
    .addDOMClass(
      generateElementSelectors({
        elementType: 'DropdownMain',
        component,
        page,
        builderVersion,
      })
    )
    .addDOMClass('header')

  const spanInnerText = getSpanInnerText(searchable, component.placeholder, defaultText)
  const spanAttrs = new Attrs(attrs.static_render).addDOMClass(
    generateElementSelectors({
      elementType: 'DropdownMainContent',
      component,
      page,
      builderVersion,
    })
  )

  //* Language controller for the placeholder in the advanced dropdown when it isn't searchable
  if (!searchable && component.placeholder) {
    addLanguageController({ attrs: spanAttrs, component, keys: ['placeholder'] })
  }
  
  if (!searchable) {
    spanAttrs.addTarget(CONTROLLER, 'currentOption')
  } else {
    //* We add this for legacy reasons, we strongly believe this is useless
    spanAttrs.addTarget(CONTROLLER, 'selectValueContainer')
  }

  const searchInputAttrs = new Attrs(attrs.static_render)
  if (searchable) {
    searchInputAttrs
      .addDOMClass(
        generateElementSelectors({
          elementType: 'DropdownMainContentInput',
          component,
          page,
          builderVersion,
        })
      )
      .add('id', `input-${component.id}`)
      .add('name', `input-${component.key}`)
      .addAction(`keydown->${CONTROLLER}#advancedDropdownAccessibility`)
      .addAction(`input->${CONTROLLER}#searchInputHandler`)
      .addTarget(CONTROLLER, 'currentOption')
      .add('list', component.id)

    //* If component key exist on userData it means it has a selected option
    //* I need to take the text of the selected option
    if (userData && userData[component.key]) {
      const selectedOption = options.find(
        (option) => option.key === userData[component.key]
      ) as OptionSelectorButton
      const selectedOptionText = selectedOption?.text || ''
      const optionTextAfterTemplate = renderTemplate(selectedOptionText, userData)
      searchInputAttrs.add('value-text-showing', optionTextAfterTemplate || '')
    }

    if (component.placeholder) {
      searchInputAttrs.add('placeholder', component.placeholder)
      //* Language controller for the placeholder in the advanced dropdown when it is searchable
      addLanguageController({ attrs: searchInputAttrs, component, keys: ['placeholder'] })
    }
  }

  //* Construct select element attributes
  const selectAttrs = new Attrs(attrs.static_render)
    .addDOMClass(
      generateElementSelectors({
        elementType: 'OptionDropdown',
        component,
        page,
        elementTags: ['Dropdown'],
        builderVersion,
      })
    )
    .addParam(CONTROLLER, `key`, component.key)
    .addAction(`change->${CONTROLLER}#selectOption`)
    .addTarget(CONTROLLER, 'select')
    .add('id', `select-${component.id}`)
    .add('name', `select-${component.key}`)
  if (component.always_disabled) selectAttrs.add('disabled', 'true')
  if (component.page_reactive_classes) selectAttrs.addReactiveElement()
  if (component.multiple) selectAttrs.add('multiple', 'true')
  if (component.isRequired) selectAttrs.add('required', 'required')

  //* Obtain the list of options
  const optionsHTML =
    options
      .map((button) => {
        //* Template button text
        const buttonTextAfterTemplate = renderTemplate(button.text, userData)
        //* Construct options with it's attributes
        const buttonAttrs = getOptionsAttrs(attrs, button, component)
        return `<option ${buttonAttrs}>${buttonTextAfterTemplate}</option>`
      })
      .join('') || ''

  const datalistAttrs = new Attrs(attrs.static_render)
    .addDOMClass(
      generateElementSelectors({
        elementType: 'DropdownList',
        component,
        page,
        builderVersion,
      })
    )
    .add('id', component.id)

  const datalistInnerHtml = getDataListInnerHtml(
    component,
    page,
    attrs,
    options,
    builderVersion,
    userData
  )

  return `<div ${wrapperAttrs}>
            <div ${headerAttrs}>
              <span ${spanAttrs}>${spanInnerText}
                ${searchable ? `<input ${searchInputAttrs}>` : ''}
              </span>
              <select ${selectAttrs}>${optionsHTML}</select>
            </div>
            <datalist ${datalistAttrs}>
              ${datalistInnerHtml}
            </datalist>
          </div>`
}

function getSpanInnerText(
  searchable: boolean,
  placeholder: string | undefined,
  defaultText: string | undefined
) {
  if (!searchable) {
    //* For legacy reasons, placeholder takes precedence over defaultText
    return placeholder || defaultText || ''
  } else {
    return ''
  }
}

function getDataListInnerHtml(
  component: OptionSelector,
  page: FlowPage,
  attrs: Attrs,
  options: OptionSelectorButton[],
  builderVersion: BaseBuilderVersion,
  userData: { [key: string]: unknown } | undefined
) {
  if (!options) return ''
  let datalistInnerHtml = ''
  options.forEach((option, index) => {
    const labelText = renderTemplate(option.text, userData)
    const optionAttrs = getOptionsAttrs(attrs, option, component)
      .add('tabindex', `${index + 1}`)
      .addDOMClass(
        generateElementSelectors({
          elementType: 'DropdownListOption',
          component,
          page,
          builderVersion,
        })
      )
      .addDOMClass('option')
      .add('label', labelText || '')
      .addAction(`click->${CONTROLLER}#selectDatalistOptionOnAdvancedDropdown`)
      .addAction(`keyup->${CONTROLLER}#keyUpOnDatalistOptionOnAdvancedDropdown`)
    const labelAttrs = new Attrs(attrs.static_render)
      .addDOMClass(
        generateElementSelectors({
          elementType: 'DropdownListOptionText',
          component,
          page,
          builderVersion,
        })
      )
      .addDOMClass('label')

    datalistInnerHtml += `<div ${optionAttrs}><div ${labelAttrs}>${labelText}</div></div>`
  })
  return datalistInnerHtml
}

function getButtons(component: OptionSelector): OptionSelectorButton[] {
  let buttons: OptionSelectorButton[] = []

  if (component.preset_buttons) buttons = buttons.concat(getPresetButtons(component.preset_buttons))

  // const mainButtons =
  //   userData && component.button_repeater_key !== undefined
  //     ? userData[component.button_repeater_key]
  //     : component && component.buttons

  const mainButtons: OptionSelectorButton[] | undefined = component && component.buttons

  if (Array.isArray(mainButtons)) buttons = buttons.concat(mainButtons)

  if (component.dropdown && component.placeholder)
    buttons.unshift({
      id: '_placeholder_option',
      key: null,
      text: component.placeholder,
    })

  return buttons
}

function getOptionsAttrs(attrs: Attrs, option: OptionSelectorButton, component: OptionSelector) {
  //* Construct options with its attributes
  const optionAttrs = new Attrs(attrs.static_render)
    .add('value', option.key || '')
    .addParam(CONTROLLER, `key`, component.key)
    .addParam(CONTROLLER, `value`, option.key || '')
    .addTarget(CONTROLLER, `option`)
    .addAction(`keydown->${CONTROLLER}#advancedDropdownAccessibility`)

  //* In advanced dropdown, placeholder is an option, so we need to add the language controller for the placeholder
  if (option.id === '_placeholder_option') {
    addLanguageController({ attrs: optionAttrs, component, keys: ['placeholder'] })
  } else {
    addLanguageController({ attrs: optionAttrs, component: option, keys: ['text'] })
  }

  //* Add template controller if the text has {{
  if (option.text && option.text.includes('{{'))
    optionAttrs
      .addController('template')
      .addValueIfExists('template', 'text', encodeURIComponent(option.text || ''))

  if (option.conditions) optionAttrs.addConditions(option.conditions)
  if (option.single_select)
    optionAttrs.addParam(CONTROLLER, `single-select`, `${option.single_select}`)
  if (option.triggerEvent) {
    optionAttrs.addParam(CONTROLLER, `trigger-event`, option.triggerEvent)
    if (option.triggerEvent === 'open-url' && option.url) {
      optionAttrs.addParam(CONTROLLER, 'url', option.url)
      if (option.openUrlInNewTab) optionAttrs.addParam(CONTROLLER, 'new-tab', 'true')
    }
  }

  return optionAttrs
}
